import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { ENTITIES } from "v4/data/entities";
import { CHOICE_TYPE } from "v4/data/fieldTypes";
import { useNavigation } from "v4/features/front/navigation/contexts/NavigationContext";
import { usePolTranslation } from "v4/hooks/usePolTranslation";
import getViewOrderLabel from "v4/pages/admin/ViewOrders/contexts/utils/getViewOrderLabel";
import { NATIVE_IDENTIFIER_MAP, useViewOrdersContext } from "v4/pages/admin/ViewOrders/contexts/ViewOrdersContext";

export default function useViewOrdersSelection() {
    const {t} = usePolTranslation();
    const { viewOrders, changeCurrentViewOrder, currentViewOrder } = useViewOrdersContext();
    const { allNavigationItems } = useNavigation();
    const methods = useForm();

    useEffect(() => {
        if (!currentViewOrder) return;

        const currentViewOrderFullKey = `${currentViewOrder.viewOrderKey}.${currentViewOrder.viewOrderType}.${currentViewOrder.viewOrderSubKey}`;

        if (currentViewOrderFullKey === methods.getValues('view-orders-selection')) return;

        methods.setValue('view-orders-selection', currentViewOrderFullKey);
    }, [currentViewOrder, methods]);

    const { replace } = useHistory();

    function handleSelect(value) {
        if (!value) return;

        const currentViewOrderFullKey = currentViewOrder ? `${currentViewOrder.viewOrderKey}.${currentViewOrder.viewOrderType}.${currentViewOrder.viewOrderSubKey}` : null;
        if (value === currentViewOrderFullKey) return;

        replace({ search: `?viewOrder=${value}` });
        changeCurrentViewOrder(value);
    }

    const choices = Object.entries(viewOrders).flatMap(([viewOrderKey, viewOrder]) => {
        return ['form', 'search'].flatMap(key => {
            return Object.keys(viewOrder[key] ?? {}).map(formKey => {
                const mappedNativeId = NATIVE_IDENTIFIER_MAP[formKey];
                const navItem = allNavigationItems?.find(item =>
                    item.metadata?.tab_id === formKey ||
                    (mappedNativeId && item.native_identifier === mappedNativeId)
                );

                if (![...ENTITIES, 'business', 'individual'].includes(formKey) && !navItem) return null;

                const label = getViewOrderLabel(t(key), formKey, navItem);

                return {
                    label,
                    value: `${viewOrderKey}.${key}.${formKey}`,
                    group: navItem?.label ? `${t(viewOrderKey)} - ${navItem.label}` : t(viewOrderKey),
                }
            })
        })
            .filter(Boolean)
            .sort((a, b) => {
                // a = Formulaire, b = Recherche --> group them together
                // a = Formulaire Pexin, b = Recherche Pexin --> group them together
                // a = Formulaire Prolyse, b = Recherche Prolyse --> group them together
                // So at the end, we have [Formulaire, Recherche, Formulaire Pexin, Recherche Pexin, Formulaire Prolyse, Recherche Prolyse]
                return a.value.split('.')[2].localeCompare(b.value.split('.')[2]);
            })
    });

    const form = {
        type: "success:get-form-schema",
        metadata: {},
        data: {
            "view-orders-selection": {
                attr: {},
                constraints: {},
                label: false,
                name: "view-orders-selection",
                type: CHOICE_TYPE,
                choices,
                onFieldChange: handleSelect,
                specificParams: {
                    overrideStyles: (style) => ({
                        ...style,
                        control: (base) => ({
                            ...style.control(base),
                            backgroundColor: 'white',
                        }),
                    })
                }
            },
        }
    }

    return {
        methods,
        form,
    }
}
